import React from 'react';
import PropTypes from 'prop-types';
import useAdminAssets from '../../../hooks/useAdminAssets';
import useFilesInScope from '../../../hooks/useFilesInScope';
import FilesInScopeList from './FilesInScopeList';

export default function RunInputColumn({
  assetId, onAssetChange, question, onQuestionChange, mode, onModeChange, onRun, running,
}) {
  const { data: assets } = useAdminAssets();
  const { files, loading: filesLoading } = useFilesInScope(assetId);

  const canRun = Boolean(assetId) && question.trim().length > 0 && !running;

  return (
    <div className="d-flex flex-column gap-3">
      <div>
        <label htmlFor="rag-asset" className="form-label small fw-semibold">Asset</label>
        <select
          id="rag-asset"
          className="form-select form-select-sm"
          value={assetId || ''}
          onChange={(e) => onAssetChange(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">Select an asset…</option>
          {(assets || []).map((asset) => (
            <option key={asset.id} value={asset.id}>
              {asset.asset_name}{asset.asset_model ? ` — ${asset.asset_model}` : ''}
            </option>
          ))}
        </select>
        <div className="form-text">
          Retrieval is scoped by this asset's tags, exactly as it is for a field engineer.
        </div>
      </div>

      <div>
        <label htmlFor="rag-question" className="form-label small fw-semibold">Question</label>
        <textarea
          id="rag-question"
          className="form-control form-control-sm"
          rows={4}
          value={question}
          onChange={(e) => onQuestionChange(e.target.value)}
          placeholder="Ask what a field engineer would ask…"
        />
      </div>

      <fieldset>
        <legend className="form-label small fw-semibold">Mode</legend>
        <div className="form-check">
          <input
            className="form-check-input"
            type="radio"
            name="rag-mode"
            id="rag-mode-retrieve"
            checked={mode === 'retrieve'}
            onChange={() => onModeChange('retrieve')}
          />
          <label className="form-check-label small" htmlFor="rag-mode-retrieve">
            Retrieve only
            <span className="text-muted d-block">One embedding. No completion charged.</span>
          </label>
        </div>
        <div className="form-check">
          <input
            className="form-check-input"
            type="radio"
            name="rag-mode"
            id="rag-mode-answer"
            checked={mode === 'answer'}
            onChange={() => onModeChange('answer')}
          />
          <label className="form-check-label small" htmlFor="rag-mode-answer">
            Retrieve and answer
            <span className="text-muted d-block">Runs the full pipeline, including the reply.</span>
          </label>
        </div>
      </fieldset>

      <button type="button" className="btn btn-primary" onClick={onRun} disabled={!canRun}>
        {running ? 'Running…' : 'Run test'}
      </button>

      <div className="border-top pt-3">
        <h6 className="small fw-semibold mb-2">Files in scope</h6>
        <FilesInScopeList files={files} loading={filesLoading} assetSelected={Boolean(assetId)} />
      </div>
    </div>
  );
}

RunInputColumn.propTypes = {
  assetId: PropTypes.number,
  onAssetChange: PropTypes.func.isRequired,
  question: PropTypes.string.isRequired,
  onQuestionChange: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['retrieve', 'answer']).isRequired,
  onModeChange: PropTypes.func.isRequired,
  onRun: PropTypes.func.isRequired,
  running: PropTypes.bool,
};

RunInputColumn.defaultProps = { assetId: null, running: false };
